import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}


















(function () {
  const mq = window.matchMedia("(min-width: 750px)");

  function initHoverZoom() {
    // Update selectors if needed (these are common in Shopify product pages)
    const candidates = document.querySelectorAll(
      [
        ".product__media img",
        ".product-media img",
        ".product__media-item img",
        "[data-product-media] img",
        "[data-media-id] img"
      ].join(",")
    );

    candidates.forEach((img) => {
      // Avoid zoom on tiny thumbnails / icon images
      if (!img || img.dataset.hoverZoomInit === "1") return;

      const wrap =
        img.closest(".product__media, .product-media, .product__media-item, [data-product-media], [data-media-id]") ||
        img.parentElement;

      if (!wrap) return;

      // Make sure wrapping element clips overflow
      wrap.classList.add("hover-zoom-wrap");

      // Mark initialized
      img.dataset.hoverZoomInit = "1";

      function onEnter() {
        wrap.classList.add("is-zoomed");
      }

      function onLeave() {
        wrap.classList.remove("is-zoomed");
        img.style.transformOrigin = "50% 50%";
      }

      function onMove(e) {
        // Use wrapper bounds to calculate mouse position %
        const rect = wrap.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;

        // Clamp to 0–100 to avoid weird edges
        const cx = Math.max(0, Math.min(100, x));
        const cy = Math.max(0, Math.min(100, y));

        img.style.transformOrigin = `${cx}% ${cy}%`;
      }

      // Only mouse devices
      wrap.addEventListener("mouseenter", onEnter);
      wrap.addEventListener("mouseleave", onLeave);
      wrap.addEventListener("mousemove", onMove);
    });
  }

  function run() {
    // Only init on desktop; if resizing to mobile, remove zoom classes
    if (mq.matches) {
      initHoverZoom();
    } else {
      document.querySelectorAll(".hover-zoom-wrap.is-zoomed").forEach((el) => el.classList.remove("is-zoomed"));
    }
  }

  // Run on load
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", run);
  } else {
    run();
  }

  // If Horizon swaps media via JS, re-run after variant changes / section reloads
  document.addEventListener("variant:change", run);
  document.addEventListener("shopify:section:load", run);

  // Re-run on resize breakpoint changes
  mq.addEventListener?.("change", run);
})();
